import {
  Component,
  OnDestroy,
  OnInit,
  ViewChild,
  ChangeDetectorRef,
  ChangeDetectionStrategy,
  EventEmitter,
  Output,
  DestroyRef,
  inject,
  signal,
  computed
} from '@angular/core';

import { toSignal } from '@angular/core/rxjs-interop';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { Subscription, combineLatest, map } from 'rxjs';
import { ConfirmationService, MessageService } from 'primeng/api';
import { BatchService } from '../../../services/batch/batch.service';
import {
  Area,
  ContentApiService,
  DocumentType
} from '../../../services/content-api/content-api.service';
import { FeatureFlagService } from '../../../services/feature-flag/feature-flag.service';
import { SignOutService } from '../../../services/sign-out-state/sign-out-state.service';
import { UserPrivilegesService } from '../../../services/auth-privileges/auth-privileges.service';

import { BatchDocumentResponse, BatchResponse, Department } from '../../../models/sdk-interfaces';

import { ActivatedRoute, Router } from '@angular/router';
import { FronteggAuthService } from '@frontegg/angular';
import { AutoclassifierSdkService } from '../../../services/autoclassifier-sdk/autoclassifier-sdk.service';
import { DocumentFilingService } from '../../../services/document-filing/document-filing.service';
import { AuthService } from '../../../services/auth/auth.service';
import { ToastService } from '../../../services/toast-messages/toast-messages.service';

import { Document } from '../../../models/document';
import { CaptureComponent } from '../../../components/capture/capture.component';
import { Bind } from 'primeng/bind';
import { Dialog } from 'primeng/dialog';
import { FileUploaderComponent } from '../../../components/file-uploader/file-uploader.component';
import { InputText } from 'primeng/inputtext';
import { Button } from 'primeng/button';
import { WebScanningDialogComponent } from '../../../components/web-scanning-dialog/web-scanning-dialog.component';
import { DatePipe } from '@angular/common';
import { TranslatePipe } from '@ngx-translate/core';
import { BatchSettingsComponent } from '../../../components/batch-settings/batch-settings.component';

@Component({
  selector: 'app-open-batch',
  templateUrl: './open-batch.component.html',
  styleUrls: ['./open-batch.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CaptureComponent,
    BatchSettingsComponent,
    Bind,
    Dialog,
    FileUploaderComponent,
    FormsModule,
    ReactiveFormsModule,
    InputText,
    Button,
    WebScanningDialogComponent,
    DatePipe,
    TranslatePipe
  ]
})
export class OpenBatchComponent implements OnInit, OnDestroy {
  private fronteggAuthService = inject(FronteggAuthService);
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private contentService = inject(ContentApiService);
  private batchService = inject(BatchService);
  private featureFlagService = inject(FeatureFlagService);
  private fb = inject(FormBuilder);
  private confirmationService = inject(ConfirmationService);
  private cdr = inject(ChangeDetectorRef);
  private signOutService = inject(SignOutService);
  private messageService = inject(MessageService);
  private autoclassifierSdkService = inject(AutoclassifierSdkService);
  userPrivilegesService = inject(UserPrivilegesService);
  private sessionService = inject(DocumentFilingService);
  private authService = inject(AuthService);
  private toastService = inject(ToastService);

  @ViewChild(CaptureComponent) captureComponent!: CaptureComponent;
  @Output() changeEvent = new EventEmitter<any>();

  private subscription = new Subscription();
  private signOutSubscription = new Subscription();

  user?: any;
  batchId: string = '';
  currentAttachmentIndex: number = -1;
  formStates: Map<string, any> = new Map();
  documentForm!: FormGroup;
  batch = signal<BatchResponse>({} as BatchResponse);
  batchName = computed(() => this.batch().name || '');
  batchStatus = computed(() => this.batch().status || '');
  batchDocuments = signal<BatchDocumentResponse[]>([]);
  documents: Document[] = [];
  tenantId: string = '';
  loadingFilingFiles: boolean = false;
  fillingDocsIds: string[] = [];
  areas: Area[] = [];
  settingsAreas: Area[] = [];
  filteredDocumentTypes: DocumentType[] = [];
  batchEditFilteredDocumentTypes = signal<DocumentType[]>([]);
  currentDocumentType: DocumentType | undefined;
  showEditBatchModal: boolean = false;
  editBatchFormGroup!: FormGroup;
  showAddDocumentsModal: boolean = false;
  showScanningDialog: boolean = false;
  showMergeDocuments: boolean = false;
  statusCheckInterval: any;
  isClosingBatch: boolean = false;
  isDeletingBatch: boolean = false;
  enableBatchOptions = signal<boolean>(false);
  showUnsupportedFileMessage: boolean = false;
  currentMostConfidentDocument: any;
  showClassificationResults: boolean = false;
  batchLoading = signal<boolean>(false);
  isFetchingDocuments: boolean = false;
  isAutoClassificationEnabled = signal<boolean>(false);

  constructor() {
    this.batchId = this.route.snapshot.paramMap.get('batchId') ?? '';
  }
  destroyRef = inject(DestroyRef);

  /**
   * Initializes the component by setting up authentication, loading the batch,
   * configuring subscriptions, and checking feature flags.
   */
  ngOnInit(): void {
    this.tenantId = localStorage.getItem('tenantId') || '';
    const organization = localStorage.getItem('vanityName');

    if (this.tenantId && organization) {
      this.subscription.add(
        this.fronteggAuthService.authState$.subscribe(authState => {
          if (authState.user?.accessToken) {
            this.user = authState.user;
          }
        })
      );
    }
    this.batch.update(b => ({ ...b, batchId: this.batchId }));
    this.openBatch(this.batchId);
    this.signOutService.isFilingContent = true;

    this.signOutSubscription = this.signOutService.signOut$.subscribe(signOut => {
      if (signOut) {
        this.canDeactivate();
      }
    });

    this.checkAutoClassificationFeatureFlag();
  }

  /**
   * Stops any active status check intervals, unsubscribes from all
   * subscriptions to prevent memory leaks, and resets the document
   * fetching state.
   */
  ngOnDestroy(): void {
    this.endStatusCheckInterval();
    this.subscription.unsubscribe();
    this.signOutSubscription.unsubscribe();
    this.isFetchingDocuments = false;
  }

  /**
   * Checks if automatic document classification is enabled via feature flags.
   */
  checkAutoClassificationFeatureFlag() {
    this.subscription.add(
      this.featureFlagService.getFlag('license-IDP', true).subscribe(idpFlagValue => {
        this.isAutoClassificationEnabled.set(Boolean(idpFlagValue));
      })
    );
  }

  /**
   * Retrieves batch details and documents, then checks out the batch if needed.
   * @param {string} batchId - The unique identifier of the batch to open
   */
  async openBatch(batchId: string): Promise<void> {
    this.batchLoading.set(true);
    this.formStates.clear();
    this.documents = [];
    this.sessionService.reset();

    const token = await this.authService.waitForToken();
    if (!token) {
      this.batchLoading.set(false);
      this.toastService.error('Error', 'authenticationError');
      return;
    }

    this.batchService.getBatch(batchId).then(response => {
      this.loadingFilingFiles = true;
      const batch = response;
      this.batch.set(batch);

      // if get batch is successful, get batch documents
      this.batchService.getBatchDocuments(this.batch().batchId!).then(response => {
        // Ensure documents are fetched before proceeding

        this.batchDocuments.set(response.items);

        // make list of document ids
        this.fillingDocsIds = response.items.map(item => item.documentId);

        if (this.batch().status !== 'completed' && this.batch().status !== 'checked_out') {
          // check out the batch
          this.checkOutBatch(this.batch().batchId!).then(() => {
            // Now compute the enableBatchOptions flag using privilege checks.
            this.runPrivilegeChecks();
          });
        } else {
          // For completed batches, skip checkout and run privilege checks immediately.
          this.runPrivilegeChecks();
        }

        this.initializeDocumentForm();
        this.captureComponent.startAdHocFiling(this.fillingDocsIds);
        this.batchLoading.set(false);
      });
    });
  }

  /**
   * Runs the privilege checks to compute enableBatchOptions.
   */
  private runPrivilegeChecks(): void {
    combineLatest([
      this.userPrivilegesService.canPerformAction(
        'EDIT_BATCH',
        this.batch().department,
        this.batch().status
      ),
      this.userPrivilegesService.canPerformAction(
        'CHANGE_BATCH_ACCESS',
        this.batch().department,
        this.batch().status
      ),
      this.userPrivilegesService.canPerformAction(
        'MANAGE_BATCH_DOCUMENTS',
        this.batch().department,
        this.batch().status
      ),
      this.userPrivilegesService.canPerformAction(
        'DELETE_BATCH',
        this.batch().department,
        this.batch().status
      )
    ])
      .pipe(
        map(([canEdit, canChange, canManage, canDelete]) => {
          // For add documents, enable if user has manage privilege
          const showAddDocuments = canManage;
          return canEdit || canChange || showAddDocuments || canDelete;
        })
      )
      .subscribe(result => {
        this.enableBatchOptions.set(result);
      });
  }

  /**
   * Checks if the batch is completed
   * @returns {boolean} - Returns true if the batch is completed, otherwise false
   */
  isBatchCompleted(): boolean {
    return this.batch().status === 'completed';
  }

  /**
   * Checks in the current batch
   * @returns {Promise<void>} Promise that resolves when the batch is checked in
   * @throws Will propagate any errors from the batch service
   */
  checkInBatch(): Promise<void> {
    return this.batchService
      .checkinBatch(this.batch().batchId!)
      .then(() => {
        if (this.isClosingBatch) {
          this.router.navigate(['/webcapture', 'batches']);
        }
      })
      .catch(error => {
        console.error('Error checking in batch:', error);
        throw error;
      });
  }

  /**
   * Closes the current batch and returns to the batch list
   */
  async closeBatch(): Promise<void> {
    this.isClosingBatch = true;

    //CONSIDER sending an event to capture to then save the form, SHOULDN't need to though

    if (this.isBatchCompleted()) {
      this.router.navigate(['/webcapture', 'batches'], { queryParams: { tab: 'completed' } });
    } else {
      this.checkInBatch();
    }
  }

  /**
   * Opens the edit batch modal dialog
   */
  openEditBatchModal(): void {
    this.showEditBatchModal = true;
    this.editBatchFormGroup = this.fb.group({
      name: [''],
      department: [''],
      defaultArea: [''],
      defaultDocumentType: ['']
    });

    if (this.isBatchCompleted()) {
      this.editBatchFormGroup.disable();
    } else {
      this.userPrivilegesService
        .canPerformAction('EDIT_BATCH', this.batch().department)
        .subscribe(canEdit => {
          if (!canEdit) {
            this.editBatchFormGroup.controls['name'].disable();
            this.editBatchFormGroup.controls['defaultArea'].disable();
            this.editBatchFormGroup.controls['defaultDocumentType'].disable();
          }
        });

      this.userPrivilegesService
        .canPerformAction('CHANGE_BATCH_ACCESS', this.batch().department, this.batch().status)
        .subscribe(canChange => {
          if (!canChange) {
            this.editBatchFormGroup.controls['department'].disable();
          }
        });
    }

    this.editBatchFormGroup.patchValue({
      name: this.batch().name,
      department: this.batch().department ? this.batch().department : '',
      defaultArea: this.batch().defaultArea ? this.batch().defaultArea : '',
      defaultDocumentType: this.batch().defaultDocumentType ? this.batch().defaultDocumentType : ''
    });
  }

  /**
   * Closes the edit batch modal dialog
   */
  closeEditBatchModal(): void {
    this.batchEditFilteredDocumentTypes.set([]);
    this.showEditBatchModal = false;
  }

  /**
   * Saves updated batch settings to the server
   */
  saveBatchSettings(): void {
    this.batchService
      .updateBatch(this.batch().batchId!, {
        name: this.editBatchFormGroup.controls['name'].value,
        version: this.batch().version,
        defaultDocumentType: this.editBatchFormGroup.controls['defaultDocumentType'].value ?? '',
        department: this.editBatchFormGroup.controls['department'].value,
        defaultArea: this.editBatchFormGroup.controls['defaultArea'].value ?? ''
      })
      .then(response => {
        this.batch.set(response);

        this.closeEditBatchModal();
      })
      .catch(error => {
        console.error('Error updating batch:', error);
        this.showErrorToast('updateBatchError');
      });
  }

  /**
   * Controls the visibility of the add documents modal dialog
   * @param event - Boolean value indicating whether to show the add documents modal
   */
  openAddDocumentsModal(event: boolean): void {
    this.showAddDocumentsModal = event;
  }

  /**
   * Controls the visibility of the scanning dialog
   * @param event - Boolean value indicating whether to show the scanning dialog
   */
  openScanningDialog(event: boolean): void {
    this.showScanningDialog = event;
  }

  /**
   * Handles files received from the scanning process.
   * @param files - Array of scanned File objects to be processed.
   */
  onScannedFiles(files: File[]): void {
    this.captureComponent.addScannedDocumentsToBatch(files);
  }

  /**
   * Checks out a batch for exclusive editing
   * @param {string} batchId - Unique identifier of the batch to check out
   * @returns {Promise<BatchResponse>} Promise that resolves with the checked out batch
   * @throws Will display an error message if checkout fails
   */
  checkOutBatch(batchId: string): Promise<BatchResponse> {
    return this.batchService
      .checkoutBatch(batchId)
      .then(response => {
        this.batch.set(response);
        return response;
      })
      .catch(error => {
        console.error('Error checking out batch:', error);
        throw error;
      });
  }

  /**
   * Displays a confirmation dialog for batch deletion
   */
  confirmDeleteBatch(): void {
    this.confirmationService.confirm({
      message: '',
      key: 'deleteBatchConfirmDialog',
      accept: () => {
        this.isDeletingBatch = true;
        this.deleteBatch();
      }
    });
  }

  /**
   * Deletes the current batch from the system
   */
  deleteBatch(): void {
    this.endStatusCheckInterval();
    this.batchService
      .deleteBatch(this.batch().batchId!)
      .then(() => {
        const authToken = this.authService.getAuthToken();
        this.documents.forEach(doc => {
          this.autoclassifierSdkService.documentManagementSdk
            .deleteDocument(this.tenantId, doc.fileId, authToken)
            .then(() => {});
        });
        this.router.navigate(['/webcapture', 'batches']);
      })
      .catch(error => {
        console.error('Error deleting batch:', error);
      });
  }

  /**
   * Displays a confirmation dialog for batch closure
   */
  confirmCloseBatch(): void {
    this.confirmationService.confirm({
      message: 'closeBatchWarning',
      key: 'closeBatchDialog',
      accept: () => {
        this.closeBatch();
      }
    });
  }

  /**
   * Initializes the document form with default values
   */
  initializeDocumentForm() {
    this.documentForm = this.fb.group({
      area: [''],
      documentTypeCode: ['']
    });
  }

  /**
   * Completes the batch by updating its status to 'completed' if all documents are filed.
   * Checks if all batch documents have been successfully filed before marking the batch as complete.
   * Navigates back to the batches page regardless of completion status.
   */
  completeBatch(): void {
    this.batchService.getBatchDocuments(this.batch().batchId!).then(response => {
      const allDocumentsFiled = response.items.every(doc => doc.status === 'filed');
      if (allDocumentsFiled || response.items.length === 0) {
        this.batchService
          .updateBatch(this.batch().batchId!, {
            status: 'completed',
            version: this.batch().version
          })
          .then(() => {
            this.isClosingBatch = true;
            this.router.navigate(['/webcapture', 'batches']);
          });
      } else {
        this.router.navigate(['/webcapture', 'batches']);
      }
    });
  }

  /**
   * Shows an error toast message.
   * @param {string} toastMessage - The error message to display.
   * @param {string} [toastData] - Additional data to display in the toast.
   */
  showErrorToast(toastMessage: string, toastData?: string): void {
    this.messageService.add({
      severity: 'error',
      summary: 'Error',
      detail: toastMessage,
      key: 'errorToast',
      data: toastData ? toastData : '',
      life: 10000
    });
  }

  /**
   * Focuses the close button in the dialog
   */
  onDialogShow(): void {
    const closeButton = document.querySelector('.p-dialog-header-close') as HTMLElement;
    closeButton?.focus();
  }

  /**
   * Checks if the component can be safely deactivated
   * @returns {boolean|Promise<boolean>} True if deactivation can proceed, otherwise false
   */
  canDeactivate(): boolean | Promise<boolean> {
    if (this.isClosingBatch || this.isDeletingBatch) {
      return Promise.resolve(true);
    } else {
      return new Promise(resolve => {
        this.confirmationService.confirm({
          message: 'unsavedDataWarning',
          key: 'tabChangeDialog',
          accept: () => {
            if (this.isBatchCompleted()) {
              resolve(true);
            } else {
              this.checkInBatch()
                .then(() => {
                  if (this.signOutService.getSignOutState()) {
                    this.signOutService.signOut();
                  }
                  resolve(true);
                })
                .catch(() => {
                  resolve(false);
                });
            }
          },
          reject: () => {
            if (this.signOutService.getSignOutState()) {
              this.signOutService.setSignOutState(false);
            }
            resolve(false);
          }
        });
      });
    }
  }

  /**
   * Ends the status check interval and sets the isFetchingDocuments flag to false
   */
  endStatusCheckInterval(): void {
    if (this.statusCheckInterval) {
      clearInterval(this.statusCheckInterval);
      this.statusCheckInterval = null;
    }
    this.isFetchingDocuments = false;
  }
}
